import { useEffect, useRef, useState } from 'react';
import type { ShiftManagerReportPage } from 'shared-models';
import type { ManagerReportRow } from '../../models/reports.types';
import { shiftWorkforceService } from '../../services/workforce/shift-workforce.service';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { dateKey, monthStart, minutesLabel, timeLabel } from './manager-reports-page.utils';

export function useManagerReportsPageController() {
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(() => dateKey(new Date()));
  const [employeePk, setEmployeePk] = useState<number | null>(null);
  const [rows, setRows] = useState<ManagerReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ShiftManagerReportPage['summary']>({ employees: [], totals: { worked: 0, overtime: 0, tips: '0.00' } });
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const pageLock = useRef(false);
  const exportLock = useRef(false);
  const generation = useRef(0);
  const [resync, setResync] = useState(0);
  const [isOutdated, setIsOutdated] = useState(false);
  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'sse.resync') setResync((current) => current + 1);
    else if (event.type.startsWith('attendance.') || event.type.startsWith('tip.')) setIsOutdated(true);
  });

  useEffect(() => {
    if (!eventsReady) return;
    let active = true;
    const requestGeneration = ++generation.current;
    setIsLoading(true);
    setIsOutdated(false);
    setError(null);
    void Promise.resolve().then(async () => {
      if (!active) return;
      try {
        const result = await shiftWorkforceService.getManagerReportPage(from, to, employeePk ?? undefined);
        if (active && requestGeneration === generation.current) {
          setRows(result.rows);
          setSummary(result.summary);
          setHasMore(result.pagination.hasMore);
        }
      } catch {
        if (active) setError('לא ניתן לטעון את דוח הנוכחות.');
      } finally {
        if (active) setIsLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [from, to, employeePk, eventsReady, resync]);

  const employees = summary.employees.map((employee): [number, string] => [employee.id, employee.name]);
  const filtered = rows;
  const totals = { ...summary.totals, tips: Number(summary.totals.tips) };

  const onLoadMore = async () => {
    if (pageLock.current) return;
    pageLock.current = true;
    setIsLoadingMore(true);
    const requestGeneration = generation.current;
    try {
      const next = await shiftWorkforceService.getManagerReportPage(from, to, employeePk ?? undefined, { offset: rows.length, limit: 50 });
      if (requestGeneration !== generation.current) return;
      setRows((current) => [...current, ...next.rows.filter((row) => !current.some((existing) => existing.id === row.id))]);
      setHasMore(next.pagination.hasMore);
      setSummary(next.summary);
    } catch {
      if (requestGeneration === generation.current) setError('לא ניתן לטעון רישומים נוספים.');
    } finally {
      pageLock.current = false;
      setIsLoadingMore(false);
    }
  };

  const onExportExcel = async () => {
    if (exportLock.current) return;
    exportLock.current = true;
    setIsExporting(true);
    try {
      const exportRows: ManagerReportRow[] = [];
      let hasNext = true;
      while (hasNext) {
        const next = await shiftWorkforceService.getManagerReportPage(from, to, employeePk ?? undefined, { offset: exportRows.length, limit: 100 });
        exportRows.push(...next.rows);
        hasNext = next.pagination.hasMore;
        if (hasNext && next.rows.length === 0) throw new Error('Empty report page');
      }
      const XLSX = await import('xlsx');
      const sheet = XLSX.utils.json_to_sheet(
        exportRows.map((row) => ({
          עובד: row.employeeName,
          תאריך: row.date,
          מחלקה: row.roleName,
          כניסה: timeLabel(row.actualStart),
          יציאה: timeLabel(row.actualEnd),
          'משך בפועל': minutesLabel(row.workedMinutes),
          טיפים: row.tipTotal,
        })),
      );
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Attendance');
      XLSX.writeFile(book, `shift-report-${from}-${to}.xlsx`);
    } catch {
      setError('לא ניתן לייצא את הדוח.');
    } finally {
      exportLock.current = false;
      setIsExporting(false);
    }
  };

  return {
    from,
    setFrom,
    to,
    setTo,
    employeePk,
    setEmployeePk,
    isLoading,
    hasMore,
    isLoadingMore,
    isExporting,
    onLoadMore,
    error,
    employees,
    filtered,
    totals,
    onExportExcel,
    isOutdated,
    onRefresh: () => setResync((current) => current + 1),
  };
}
